import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { CreditCard, Plus, Wifi } from 'lucide-react';
// eslint-disable-next-line

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CardPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCards();
  }, []);

  const fetchCards = async () => {
    try {
      const response = await fetch(`${API}/cards`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        
      });

      if (response.ok) {
        const data = await response.json();
        setCards(data);
      }
    } catch (error) {
      console.error('Failed to fetch cards:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="card-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            {t('dashboard.card')}
          </h1>
          <p className="text-slate-600">Manage your smart business cards</p>
        </div>
        <Link to="/products">
          <Button className="rounded-xl gap-2" data-testid="buy-card-btn">
            <Plus className="w-4 h-4" />
            Buy New Card
          </Button>
        </Link>
      </motion.div>

      {cards.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, idx) => (
            <motion.div
              key={card.card_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="bg-white rounded-2xl shadow-soft overflow-hidden"
              data-testid={`card-${card.card_id}`}
            >
              {/* Card Visual */}
              <div className="relative h-48 bg-gradient-to-br from-slate-800 to-slate-900 p-6">
                <div className="absolute top-4 right-4">
                  <Wifi className="w-6 h-6 text-white/50" />
                </div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg mb-3" />
                  <p className="text-white font-mono text-sm tracking-wider">
                    {card.serial_number}
                  </p>
                </div>
                <div className="absolute top-6 left-6">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-white rounded flex items-center justify-center">
                      <span className="text-slate-900 font-bold text-xs">IK</span>
                    </div>
                    <span className="text-white font-medium text-sm">IKATone</span>
                  </div>
                </div>
              </div>

              {/* Card Details */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium text-slate-900">{card.card_model}</p>
                    <p className="text-sm text-slate-500 capitalize">
                      {card.color} / {card.design}
                    </p>
                  </div>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                    card.activated 
                      ? 'bg-[#00C48C]/10 text-[#00A67E]' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {card.activated ? t('dashboard.cardActivated') : 'Inactive'}
                  </span>
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Added</span>
                    <span className="text-slate-900">
                      {new Date(card.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-soft p-12 text-center"
        >
          <div className="w-20 h-20 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <CreditCard className="w-10 h-10 text-slate-400" />
          </div>
          <h2 className="font-heading text-xl font-semibold text-slate-900 mb-2">
            {t('dashboard.noCards')}
          </h2>
          <p className="text-slate-600 mb-6 max-w-sm mx-auto">
            Purchase your first IKATone card to start sharing your profile with a simple tap.
          </p>
          <Link to="/products">
            <Button className="btn-primary" data-testid="buy-first-card-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.buyCard')}
            </Button>
          </Link>
        </motion.div>
      )}
    </div>
  );
}
